import { html } from 'common-tags';

import { fields } from './shared';

/**
 * @typedef {import('../types').Brand} Brand
 * @typedef {import('../types').Template} Template
 */

/**
 * Innovate bizcard template.
 * @param {Brand} brand - The brand object with theme colors and assets.
 * @returns {Template} - An object containing the email's HTML body and style block.
 */
export default (brand) => ({
  style: html`
    <style>
      :root {
        --background-desktop_only-background-color: #e0e0e0;
        --background-desktop_only-background-image: ;

        --card-background-color: ${brand.colorBackground};
        --card-color: ${brand.colorText};
        --card-font-family: '${brand.fontBase}';
        --card-font-weight: ${brand.fontWeightBase};
        --card-padding: 1.35rem;
        --card-desktop_only-border-radius: 0.75rem;
        --card-desktop_only-box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.25);
        --card-header-background-color: ${brand.colorPrimary};
        --card-logo-background-image: url('${brand.logoUrl}');
        --card-logo-height: 2rem;
        --card-logo-padding: 1.35rem;
        --card-logo-text-align: left;

        --profile_image-background-image: ;

        --qr_code-display: block;
        --qr_code-border-radius: 0.25rem;
        --qr_code-box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);

        --titles-color: ${brand.colorText};
        --titles-font-family: '${brand.fontHeadings}';
        --titles-font-size: 2rem;
        --titles-font-weight: ${brand.fontWeightHeadings};
        --titles-text-align: left;
        --titles-text-transform: none;

        --fields-icons-display: inline-block;
        --fields-icons-color: ${brand.colorText};
        --fields-icons-background-color: transparent;
        --fields-icons-padding: 0px;
        --fields-labels-display: inline-block;
        --fields-labels-color: ${brand.colorSecondary};
        --fields-font-size: 16px;
        --fields-line-height: 1.6;

        --button-background-color: ${brand.colorSecondary};
        --button-color: ${brand.colorBackground};
        --button-border-color: ${brand.colorSecondary};
        --button-border-width: 3px;
        --button-border-radius: 0.5rem;
        --button-text-transform: none;
      }

      body {
        background-color: var(--background-desktop_only-background-color);
        background-image: var(--background-desktop_only-background-image);
        background-size: cover;
        font-family: var(--card-font-family);
        font-weight: var(--card-font-weight);
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        color: var(--titles-color);
        font-family: var(--titles-font-family);
        font-weight: var(--titles-font-weight);
        text-align: var(--titles-text-align);
        text-transform: var(--titles-text-transform);
      }

      h1 {
        font-size: var(--titles-font-size);
      }

      h3 {
        font-size: calc(var(--titles-font-size) * 0.65);
      }

      nav {
        background-color: transparent;
        padding: 0;
      }

      #logo {
        background-image: var(--card-logo-background-image);
        background-position: var(--card-logo-text-align) center;
        background-repeat: no-repeat;
        background-size: contain;
        height: var(--card-logo-height);
        width: 100%;
      }

      .card {
        background-color: var(--card-background-color);
        color: var(--card-color);
        margin: 0 auto;
        min-height: 100vh;
        overflow: hidden;
        width: 100%;
      }

      .card a {
        color: var(--card-color);
        text-decoration: none;
      }

      @media (min-width: 576px) {
        nav {
          margin-bottom: 1rem;
        }

        .card {
          border-radius: var(--card-desktop_only-border-radius);
          box-shadow: var(--card-desktop_only-box-shadow);
          min-height: 0;
          width: 3.5in;
        }
      }

      .card header {
        background-color: var(--card-header-background-color);
        color: var(--card-header-color);
        padding: var(--card-logo-padding);
        position: relative;
      }

      .card .content {
        padding: var(--card-padding);
      }

      .card footer {
        padding-top: var(--card-padding);
      }

      .card button {
        background-color: var(--button-background-color);
        border-radius: var(--button-border-radius);
        color: var(--button-color);
        border: var(--button-border-width) solid var(--button-border-color);
        font-size: 1.25rem;
        font-weight: var(--button-font-weight);
        padding: 0.5rem;
        text-transform: var(--button-text-transform);
        width: 100%;
      }

      .card connect-button button,
      .card install-button button {
        background-color: transparent;
        color: var(--button-border-color);
        font-size: 1rem;
        margin-top: 0.5rem;
      }

      .card .content .profile-info {
        padding-bottom: var(--card-padding);
      }

      .card .content ul li:last-child .field-name {
        padding-bottom: 0;
      }

      .profile-image {
        aspect-ratio: 1/1;
        background-color: var(--card-header-background-color);
        overflow: hidden;
        position: relative;
        width: 100%;
      }

      .field {
        align-items: center;
        display: flex;
      }

      #qr-code {
        border-radius: var(--qr_code-border-radius);
        box-shadow: var(--qr_code-box-shadow);
        display: var(--qr_code-display);
        image-rendering: pixelated;
        position: absolute;
        right: 1rem;
        bottom: 1.5rem;
        width: 5rem;
        z-index: 10;
      }

      li {
        align-items: center;
        display: grid;
        font-size: var(--fields-font-size);
        grid-template-columns: auto 1fr;
        grid-template-rows: auto auto;
        line-height: var(--fields-line-height);
      }

      li i {
        background-color: var(--fields-icons-background-color);
        border-radius: 12rem;
        color: var(--fields-icons-color);
        display: var(--fields-icons-display);
        font-size: var(--fields-font-size);
        grid-column: 1 / 2;
        grid-row: 1 / 2;
        margin-right: 0.35rem;
        padding: var(--fields-icons-padding);
        text-align: center;
      }

      li a,
      li span {
        align-self: end;
        grid-column: 2 / 3;
        grid-row: 1 / 2;
      }

      li small {
        align-self: start;
        color: var(--fields-labels-color);
        display: var(--fields-labels-display);
        font-size: 0.8em;
        grid-column: 2 / 3;
        grid-row: 2 / 3;
      }
    </style>
  `,
  body: html`
    <nav data-element="header"></nav>

    <div class="card">
      <div class="profile-image" data-element="profile-image">
        <profile-image src="{{ image_url }}"></profile-image>
        <share-button>
          <img id="qr-code" data-element="qr-code" src="qrcode.png?launchvcard=1&quietZone=20" alt="QR Code" />
        </share-button>
      </div>
      <header data-element="card-header">
        <a href="{{ website }}" target="_blank">
          <div id="logo" data-element="card-logo"></div>
        </a>
      </header>
      <div class="content">
        <div class="profile-info" data-element="titles">
          <h1>{{ name }}</h1>
          <h3>{{ title }}</h3>
        </div>

        ${fields}

        <footer data-element="button">
          <user-only>
            <share-button>
              <button>Share</button>
            </share-button>
            <install-button>
              <button>Add to Home Screen</button>
            </install-button>
          </user-only>

          <shared-only>
            <vcard-button>
              <button>Add Contact</button>
            </vcard-button>
          </shared-only>
        </footer>
      </div>
    </div>
  `
});
